import React from 'react';
import { Dispatch } from 'redux';
import { ForeignActivitiesIndirectItemModel } from 'forms/sections/foreign/activities/indirect/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import { CheckboxModel } from 'forms/shared/types';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import NotApplicable from 'components/Form/NotApplicable';
import Show from 'components/Form/Show';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Text from 'components/Form/Text';
import { getSafeDateTime } from 'helpers/date';
import ErrorMessage from 'components/ErrorMessage';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { indirectInterestAddressNameFields } from 'constants/enums/people';
import { isPVQForm } from 'helpers/form';
import Country from 'components/Form/Country';
import Dropdown from 'components/Form/Dropdown';
import PVQName from 'components/Form/PVQName';
import { foreignContactRelationshipOptions, foreignFinancialInterestOptions } from 'constants/enums/foreignActivityOptions';
import CoOwners from '../Shared/CoOwners';

interface Props extends Partial<ForeignActivitiesIndirectItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  dispatch: Dispatch
  prefix: string
}

export default class IndirectInterest extends ValidationElement<Props> {
  update = (queue: Partial<ForeignActivitiesIndirectItemModel>) => {
    this.props.onUpdate({
      InterestTypes: this.props.InterestTypes,
      InterestType: this.props.InterestType,
      Firstname: this.props.Firstname,
      Lastname: this.props.Lastname,
      Acquired: this.props.Acquired,
      RelationshipNature: this.props.RelationshipNature,
      HowAcquired: this.props.HowAcquired,
      Cost: this.props.Cost,
      CostEstimated: this.props.CostEstimated,
      Value: this.props.Value,
      ValueEstimated: this.props.ValueEstimated,
      Sold: this.props.Sold,
      SoldNotApplicable: this.props.SoldNotApplicable,
      Explanation: this.props.Explanation,
      CoOwners: this.props.CoOwners,

      // pvq
      Name: this.props.Name,
      InterestTypeExplanation: this.props.InterestTypeExplanation,
      Country: this.props.Country,
      FinancialInterestType: this.props.FinancialInterestType,
      RelationshipType: this.props.RelationshipType,

      ...queue,
    });
  };

  updateInterestTypes = (values: CheckboxModel) => {
    const interestType = values.value;
    const selected = [...((this.props.InterestTypes || {}).values || [])];
    if (selected.includes(interestType)) {
      selected.splice(selected.indexOf(interestType), 1);
    } else {
      selected.push(interestType);
    }

    this.update({
      InterestTypes: { values: selected },
    });
  };

  updateInterestType = (values: ForeignActivitiesIndirectItemModel['InterestType']) => {
    this.update({
      InterestType: values,
    });
  };

  updateFirstname = (values: ForeignActivitiesIndirectItemModel['Firstname']) => {
    this.update({
      Firstname: values,
    });
  };

  updateLastname = (values: ForeignActivitiesIndirectItemModel['Lastname']) => {
    this.update({
      Lastname: values,
    });
  };

  updateRelationshipNature = (values: ForeignActivitiesIndirectItemModel['RelationshipNature']) => {
    this.update({
      RelationshipNature: values,
    });
  };

  updateAcquired = (values: ForeignActivitiesIndirectItemModel['Acquired']) => {
    this.update({
      Acquired: values,
    });
  };

  updateHowAcquired = (values: ForeignActivitiesIndirectItemModel['HowAcquired']) => {
    this.update({
      HowAcquired: values,
    });
  };

  updateCost = (values: ForeignActivitiesIndirectItemModel['Cost']) => {
    this.update({
      Cost: values,
    });
  };

  updateCostEstimated = (values: ForeignActivitiesIndirectItemModel['CostEstimated']) => {
    this.update({
      CostEstimated: values,
    });
  };

  updateValue = (values: ForeignActivitiesIndirectItemModel['Value']) => {
    this.update({
      Value: values,
    });
  };

  updateValueEstimated = (values: ForeignActivitiesIndirectItemModel['ValueEstimated']) => {
    this.update({
      ValueEstimated: values,
    });
  };

  updateSold = (values: ForeignActivitiesIndirectItemModel['Sold']) => {
    this.update({
      Sold: values,
    });
  };

  updateSoldNotApplicable = (values: ForeignActivitiesIndirectItemModel['SoldNotApplicable']) => {
    this.update({
      SoldNotApplicable: values,
      Sold: values.applicable ? this.props.Sold : {},
      Explanation: values.applicable ? this.props.Explanation : {},
    });
  };

  updateExplanation = (values: ForeignActivitiesIndirectItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateCoOwners = (values: ForeignActivitiesIndirectItemModel['CoOwners']) => {
    this.update({
      CoOwners: values,
    });
  };

  updateName = (values: ForeignActivitiesIndirectItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateInterestTypeExplanation = (values: ForeignActivitiesIndirectItemModel['InterestTypeExplanation']) => {
    this.update({
      InterestTypeExplanation: values,
    });
  };

  updateCountry = (values: ForeignActivitiesIndirectItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateFinancialInterestType = (values: ForeignActivitiesIndirectItemModel['FinancialInterestType']) => {
    this.update({
      FinancialInterestType: values,
    });
  };

  updateRelationshipType = (values: ForeignActivitiesIndirectItemModel['RelationshipType']) => {
    this.update({
      RelationshipType: values,
    });
  };

  render() {
    const {
      prefix, Sold, Explanation, peopleWhoKnowYou
    } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const applicantBirthdateComparable = getSafeDateTime(this.props.applicantBirthdate);
    const acquiredDateComparable = getSafeDateTime(this.props.Acquired);
    const includeNAConfirmation = Boolean(Sold?.day || Sold?.month || Sold?.year || Explanation?.value);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(
      [{
        Name: `${this.props.Firstname?.value} ${this.props.Lastname?.value}`,
      }],
      peopleWhoKnowYou,
      indirectInterestAddressNameFields
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPvq = isPVQForm(this.props.formType);
    const isOther = (value: string) => value === 'Other';
    const includesOther = (value: string) => typeof value === 'string' && value.includes('Other');

    return isPvq ? (
      <div className="interest">

        <Field
          title={i18n.t('foreign.pvq.activities.indirect.collection.who')}
          attribute={`${attribute}.Name`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>
        <Field
          title={i18n.t('foreign.pvq.contacts.heading.relationship')}
          attribute={`${attribute}.RelationshipType`}

        >

          <Dropdown
            attribute={`${attribute}.RelationshipType`}
            name="RelationshipType"
            onError={this.props.onError}
            onUpdate={this.updateRelationshipType}
            value={this.props.RelationshipType?.value}
            required={this.props.required}
          >
            {foreignContactRelationshipOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>

        </Field>
        <Field
          title={i18n.t('foreign.pvq.activities.indirect.collection.what')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FinancialInterestType`}
        >
          <Dropdown
            attribute={`${attribute}.FinancialInterestType`}
            name="financial-interst-type"
            onError={this.props.onError}
            onUpdate={this.updateFinancialInterestType}
            value={this.props.FinancialInterestType?.value}
            required={this.props.required}
          >
            {foreignFinancialInterestOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
          {includesOther(this.props.FinancialInterestType?.value) && (
            <Field
              title="Please explain."
              attribute={`${attribute}.InterestTypeExplanation`}
            >

              <Textarea
                name="explanation"

                className="interest-type-explanation"
                {...this.props.InterestTypeExplanation}
                onUpdate={this.updateInterestTypeExplanation}
                onError={this.handleError}
                required={this.props.required}
                attribute={`${attribute}.InterestTypeExplanation`}
              />
            </Field>

          )}
        </Field>

        <Field
          title={i18n.t('foreign.pvq.activities.indirect.interest.heading.valueother')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Value`}
        >
          <Currency
            name="Value"
            className="value"
            {...this.props.Value}
            onUpdate={this.updateValue}
            min="0"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Value`}
          />

        </Field>

        <Field
          title={i18n.t('foreign.pvq.activities.indirect.interest.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            {...this.props.Country}
            name="Country"
            excludeUnitedStates
            className="country"
            onUpdate={this.updateCountry}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>
        <Field
          title={i18n.t('foreign.pvq.activities.indirect.interest.heading.whyControl')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            name="Explanation"
            className="explanation"
            {...this.props.Explanation}
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>

      </div>
    ) : (

      <div className="interest">
        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.interestTypes')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestTypes`}
        >
          <p>{i18n.t('foreign.activities.indirect.interest.para.checkAll')}</p>
          <CheckboxGroup
            className="interest-types option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.InterestTypes || {}).values}
            attribute={`${attribute}.InterestTypes`}
          >
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.indirect.interest.label.interestTypes.yourself')}
              value="Yourself"
              className="yourself"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.indirect.interest.label.interestTypes.spouse')}
              value="Spouse"
              className="spouse"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.indirect.interest.label.interestTypes.cohabitant')}
              value="Cohabitant"
              className="cohabitant"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t(
                'foreign.activities.indirect.interest.label.interestTypes.dependentChildren'
              )}
              value="DependentChildren"
              className="dependent-children"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
          </CheckboxGroup>
        </Field>

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.interestType')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestType`}
          exactAttribute
        >
          <Text
            name="InterestType"
            className="interest-type"
            {...this.props.InterestType}
            onUpdate={this.updateInterestType}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.InterestType`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.name')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Firstname`}
        >
          <Text
            name="Firstname"
            label={i18n.t('foreign.activities.indirect.interest.label.firstname')}
            {...this.props.Firstname}
            pattern="^[a-zA-Z\-\.' ]*$"
            prefix="name.first"
            onUpdate={this.updateFirstname}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Firstname`}
          />
          <Field attribute={`${attribute}.Lastname`}>
            <Text
              name="Lastname"
              label={i18n.t('foreign.activities.indirect.interest.label.lastname')}
              {...this.props.Lastname}
              pattern="^[a-zA-Z\-\.' ]*$"
              prefix="name.last"
              onUpdate={this.updateLastname}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Lastname`}
            />
          </Field>
        </Field>

        {(duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)}

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.relationship')}
          help="foreign.activities.indirect.interest.help.relationship"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RelationshipNature`}
        >
          <Textarea
            name="RelationshipNature"
            {...this.props.RelationshipNature}
            onUpdate={this.updateRelationshipNature}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.RelationshipNature`}
          />
        </Field>

        {
          Array.isArray(this.props.InterestTypes?.values) && this.props.InterestTypes?.values?.includes('Yourself')
          && applicantBirthdateComparable.valid && acquiredDateComparable.valid
          && applicantBirthdateComparable.datetime > acquiredDateComparable.datetime && (
            <ErrorMessage
              isWarning
              title="Oops, there may be a problem."
              message={[
                'This is before your birthdate. Is this your intention?',
                'Please check your answer.'
              ]}
            />
          )
        }
        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.acquired')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Acquired`}
        >
          <DateControl
            name="Acquired"
            className="acquired"
            {...this.props.Acquired}
            minDateEqualTo
            onUpdate={this.updateAcquired}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Acquired`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.cost')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Cost`}
        >
          <Currency
            name="Cost"
            className="cost"
            {...this.props.Cost}
            onUpdate={this.updateCost}
            min="0"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Cost`}
          />
          <div className="flags">
            <Checkbox
              name="CostEstimated"
              label={i18n.t('foreign.activities.indirect.interest.label.costEstimated')}
              toggle="false"
              {...this.props.CostEstimated}
              onUpdate={this.updateCostEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.CostEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.howAcquired')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HowAcquired`}
        >
          <p>{i18n.t('foreign.activities.indirect.interest.para.howAcquired')}</p>
          <Textarea
            name="HowAcquired"
            className="how-acquired"
            {...this.props.HowAcquired}
            onUpdate={this.updateHowAcquired}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.HowAcquired`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.value')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Value`}
        >
          <Currency
            name="Value"
            className="value"
            {...this.props.Value}
            onUpdate={this.updateValue}
            min="0"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Value`}
          />
          <div className="flags">
            <Checkbox
              name="ValueEstimated"
              label={i18n.t('foreign.activities.indirect.interest.label.valueEstimated')}
              toggle="false"
              {...this.props.ValueEstimated}
              onUpdate={this.updateValueEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.ValueEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.indirect.interest.heading.sold')}
          help="foreign.activities.indirect.interest.help.sold"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Sold`}
        >
          <NotApplicable
            name="SoldNotApplicable"
            {...this.props.SoldNotApplicable}
            label={i18n.t('foreign.activities.indirect.interest.label.soldNotApplicable')}
            or={i18n.m('foreign.activities.indirect.interest.label.or')}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateSoldNotApplicable}
            attribute={`${attribute}.SoldNotApplicable`}
            includeNAConfirmation={includeNAConfirmation}
          >
            <DateControl
              name="Sold"
              className="sold"
              {...this.props.Sold}
              minDate={this.props.Acquired}
              minDateEqualTo
              prefix="indirectControl"
              onUpdate={this.updateSold}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Sold`}
            />
          </NotApplicable>
        </Field>

        <Show when={(this.props.SoldNotApplicable || {}).applicable}>
          <Field
            title={i18n.t('foreign.activities.indirect.interest.heading.explanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Explanation`}
          >
            <Textarea
              name="Explanation"
              className="explanation"
              {...this.props.Explanation}
              onUpdate={this.updateExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Explanation`}
            />
          </Field>
        </Show>

        <CoOwners
          prefix={prefix}
          {...this.props.CoOwners}
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateCoOwners}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CoOwners`}
          peopleWhoKnowYou={this.props.peopleWhoKnowYou}
        />
      </div>
    );
  }
}

IndirectInterest.defaultProps = {
  SoldNotApplicable: { applicable: true },
  prefix: 'activities.indirect.interest',
  addressBooks: {},
  applicantBirthdate: {},
  dispatch: () => { },
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode) => arr,

  // pvq
  Name: {},
  InterestTypeExplanation: {},
  Country: {},
  FinancialInterestType: {},

};
